import { Box, Button } from '@mui/material';
import { ChevronsUpDown } from 'lucide-react';
import { forwardRef, MouseEvent, ReactNode } from 'react';

interface BreadcrumbButtonProps {
    topContent: ReactNode;
    label: ReactNode;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    active?: boolean;
    flipped?: boolean;
    'data-testid'?: string;
}

const baseRowStyles = {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
} as const;

export const breadcrumbTopContentStyles = {
    ...baseRowStyles,
    fontSize: 11,
    color: 'text.secondary',
} as const;

export const breadcrumbLabelStyles = {
    ...baseRowStyles,
    fontSize: 12,
    fontWeight: 500,
    color: 'text.primary',
    gap: 0.5,
} as const;

const BreadcrumbButton = forwardRef<HTMLButtonElement, BreadcrumbButtonProps>(
    function BreadcrumbButton(
        {
            topContent,
            label,
            onClick,
            active = false,
            flipped = false,
            'data-testid': testId,
        },
        ref
    ) {
        const topStyles = flipped
            ? breadcrumbLabelStyles
            : breadcrumbTopContentStyles;
        const bottomStyles = flipped
            ? breadcrumbTopContentStyles
            : breadcrumbLabelStyles;

        return (
            <Button
                ref={ref}
                size="small"
                onClick={onClick}
                data-testid={testId}
                variant="outlined"
                sx={(theme) => ({
                    height: 40,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    gap: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    ...(active && {
                        backgroundColor: theme.palette.action.selected,
                    }),
                })}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                >
                    <Box component="span" sx={topStyles}>
                        {topContent}
                    </Box>
                    <Box component="span" sx={bottomStyles}>
                        {label}
                    </Box>
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                    <ChevronsUpDown size={11} style={{ marginRight: -3 }} />
                </Box>
            </Button>
        );
    }
);

export default BreadcrumbButton;
